import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { X, ChevronLeft, ChevronRight, AlertTriangle, ShieldAlert, Flag, Download } from 'lucide-react';
import { dataRoomAPI } from '../../../services/api';

/**
 * Secure File Preview Modal (Phase 3)
 *
 * Features:
 *  - Fetches /preview-info to determine effective permission + page count
 *  - For view_only users: renders page images (PNG per page) - no raw bytes.
 *  - For view_download / view_download_print: renders watermarked PDF in an iframe.
 *  - Disables right-click, prevents copy/print at the DOM level.
 *  - Logs view-start / view-end to the backend for time-spent analytics.
 *  - Admin users can flag a file as "high-value" for access alerts.
 */
export default function FilePreviewModal({ open, onClose, dataRoomId, file, canEdit, externalJwt = null }) {
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [imgSrc, setImgSrc] = useState(null);
  const [pdfBlobUrl, setPdfBlobUrl] = useState(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [isHighValue, setIsHighValue] = useState(false);
  const [flagging, setFlagging] = useState(false);

  const sessionIdRef = useRef(null);
  const viewStartRef = useRef(null);

  // Build request config with optional external JWT override
  const authConfig = React.useMemo(() => {
    return externalJwt ? { headers: { Authorization: `Bearer ${externalJwt}` } } : {};
  }, [externalJwt]);

  // Prevent right-click / copy / print globally while the modal is open and print is restricted
  useEffect(() => {
    if (!open) return;
    const blockCtx = (e) => e.preventDefault();
    const blockCopy = (e) => e.preventDefault();
    const blockKey = (e) => {
      const k = e.key.toLowerCase();
      const ctrl = e.ctrlKey || e.metaKey;
      if (ctrl && (k === 'p' || k === 's')) {
        e.preventDefault();
        if (k === 'p' && file?.id) {
          // record print attempt
          try { dataRoomAPI.recordPrintAttempt(dataRoomId, file.id, authConfig); } catch (_) {}
        }
      }
    };
    const beforePrint = (e) => {
      e.preventDefault();
      if (file?.id) {
        try { dataRoomAPI.recordPrintAttempt(dataRoomId, file.id, authConfig); } catch (_) {}
      }
    };
    document.addEventListener('contextmenu', blockCtx);
    document.addEventListener('copy', blockCopy);
    document.addEventListener('cut', blockCopy);
    document.addEventListener('keydown', blockKey);
    window.addEventListener('beforeprint', beforePrint);
    return () => {
      document.removeEventListener('contextmenu', blockCtx);
      document.removeEventListener('copy', blockCopy);
      document.removeEventListener('cut', blockCopy);
      document.removeEventListener('keydown', blockKey);
      window.removeEventListener('beforeprint', beforePrint);
    };
  }, [open, file, dataRoomId, authConfig]);

  useEffect(() => {
    if (!open || !file) return;
    setInfo(null);
    setErr('');
    setCurrentPage(1);
    setImgSrc(null);
    setPdfBlobUrl(null);
    setLoading(true);
    sessionIdRef.current = `s-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    viewStartRef.current = Date.now();

    dataRoomAPI.getPreviewInfo(dataRoomId, file.id, authConfig)
      .then(res => {
        setInfo(res.data);
        setIsHighValue(res.data.is_high_value || false);
        if (res.data.permission === 'no_access') {
          setErr('You do not have access to preview this file.');
        }
      })
      .catch(err => {
        setErr(err.response?.data?.detail || 'Failed to load preview');
      })
      .finally(() => setLoading(false));

    // Record view_start
    try {
      dataRoomAPI.recordViewEvent(dataRoomId, file.id, {
        event: 'start',
        session_id: sessionIdRef.current,
      }, authConfig);
    } catch (_) { /* silent */ }

    return () => {
      // On close/unmount: record view_end with duration
      if (!file?.id || !viewStartRef.current) return;
      const durationSec = Math.round((Date.now() - viewStartRef.current) / 1000);
      try {
        dataRoomAPI.recordViewEvent(dataRoomId, file.id, {
          event: 'end',
          session_id: sessionIdRef.current,
          duration_seconds: durationSec,
        }, authConfig);
      } catch (_) { /* silent */ }
    };
  }, [open, file, dataRoomId, authConfig]);

  // Load image for current page when in view_only OR redacted mode
  useEffect(() => {
    if (!open || !info || info.permission === 'no_access') return;
    if (info.permission !== 'view_only' && info.permission !== 'redacted') return;
    const url = dataRoomAPI.previewPageUrl(dataRoomId, file.id, currentPage);
    axios.get(url, { responseType: 'blob', ...authConfig })
      .then(res => {
        const blobUrl = URL.createObjectURL(res.data);
        setImgSrc(prev => {
          if (prev) URL.revokeObjectURL(prev);
          return blobUrl;
        });
      })
      .catch(e => setErr(e.response?.data?.detail || 'Failed to load page image'));
  }, [open, info, currentPage, file, dataRoomId, authConfig]);

  // Load watermarked PDF for view_print, view_download or view_download_print users
  useEffect(() => {
    if (!open || !info) return;
    if (!['view_print', 'view_download', 'view_download_print'].includes(info.permission)) return;
    setPdfLoading(true);
    const url = dataRoomAPI.previewPdfUrl(dataRoomId, file.id);
    axios.get(url, { responseType: 'blob', ...authConfig })
      .then(res => {
        const blobUrl = URL.createObjectURL(res.data);
        setPdfBlobUrl(prev => {
          if (prev) URL.revokeObjectURL(prev);
          return blobUrl;
        });
      })
      .catch(e => setErr(e.response?.data?.detail || 'Failed to load PDF preview'))
      .finally(() => setPdfLoading(false));
  }, [open, info, file, dataRoomId, authConfig]);

  useEffect(() => {
    return () => {
      if (imgSrc) URL.revokeObjectURL(imgSrc);
      if (pdfBlobUrl) URL.revokeObjectURL(pdfBlobUrl);
    };
  }, []);

  const handleFlag = async () => {
    if (!file?.id) return;
    setFlagging(true);
    try {
      const next = !isHighValue;
      await dataRoomAPI.flagHighValue(dataRoomId, file.id, next);
      setIsHighValue(next);
    } catch (e) {
      alert(e.response?.data?.detail || 'Failed');
    } finally {
      setFlagging(false);
    }
  };

  const handleDownload = async () => {
    if (!file?.id) return;
    try {
      const res = await dataRoomAPI.getDownloadUrl(dataRoomId, file.id, authConfig);
      const a = document.createElement('a');
      a.href = res.data.url;
      a.download = file.name;
      a.rel = 'noopener';
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (e) {
      alert('Failed to generate download URL');
    }
  };

  if (!open || !file) return null;

  const pageCount = info?.page_count || 1;
  const perm = info?.permission;

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center" data-testid="preview-modal">
      <div className="bg-white rounded-lg w-[95vw] h-[92vh] flex flex-col shadow-2xl select-none">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b bg-gray-50">
          <div className="flex items-center gap-3 min-w-0 flex-1">
            <h2 className="font-semibold text-gray-900 truncate" data-testid="preview-title">{file.name}</h2>
            {perm && (
              <span className="text-[10px] px-2 py-0.5 rounded bg-blue-50 text-blue-700 border border-blue-200 uppercase">
                {perm.replace(/_/g, ' ')}
              </span>
            )}
            {isHighValue && (
              <span className="text-[10px] px-2 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-200 inline-flex items-center gap-1">
                <Flag size={11} /> High-value
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            {canEdit && (
              <button
                onClick={handleFlag}
                disabled={flagging}
                className={`inline-flex items-center px-2.5 py-1.5 border rounded-lg text-xs ${isHighValue ? 'bg-amber-50 text-amber-700 border-amber-200' : 'hover:bg-gray-100 text-gray-600'}`}
                data-testid="flag-high-value-btn"
              >
                <Flag size={12} className="mr-1" /> {isHighValue ? 'Unflag' : 'Flag high-value'}
              </button>
            )}
            {['view_download', 'view_download_print'].includes(perm) && (
              <button
                onClick={handleDownload}
                className="inline-flex items-center px-3 py-1.5 bg-blue-600 text-white rounded-lg text-xs hover:bg-blue-700"
                data-testid="preview-download-btn"
              >
                <Download size={12} className="mr-1" /> Download
              </button>
            )}
            <button onClick={onClose} className="p-1.5 text-gray-400 hover:text-gray-700" data-testid="close-preview">
              <X size={18} />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 min-h-0 overflow-hidden bg-gray-100 relative" onContextMenu={(e) => e.preventDefault()}>
          {loading ? (
            <div className="flex items-center justify-center h-full text-gray-500">Loading preview...</div>
          ) : err ? (
            <div className="flex flex-col items-center justify-center h-full text-gray-700 p-6">
              <ShieldAlert size={40} className="text-red-400 mb-2" />
              <p className="text-sm">{err}</p>
            </div>
          ) : perm === 'view_only' ? (
            <ViewOnlyPager
              imgSrc={imgSrc}
              currentPage={currentPage}
              pageCount={pageCount}
              setCurrentPage={setCurrentPage}
            />
          ) : perm === 'redacted' ? (
            <div className="flex flex-col h-full">
              <div className="bg-amber-50 border-b border-amber-200 text-amber-800 text-[11px] px-4 py-1.5 flex items-center gap-2">
                <ShieldAlert size={12} />
                <span>
                  Sensitive data (emails, phone numbers, PAN, Aadhaar, etc.) has been automatically
                  redacted for your role. Downloads are disabled.
                </span>
              </div>
              <div className="flex-1 min-h-0">
                <ViewOnlyPager
                  imgSrc={imgSrc}
                  currentPage={currentPage}
                  pageCount={pageCount}
                  setCurrentPage={setCurrentPage}
                />
              </div>
            </div>
          ) : ['view_print', 'view_download', 'view_download_print'].includes(perm) ? (
            pdfLoading || !pdfBlobUrl ? (
              <div className="flex items-center justify-center h-full text-gray-500">Loading watermarked PDF...</div>
            ) : (
              <iframe
                title="file-preview"
                src={`${pdfBlobUrl}#toolbar=${['view_print', 'view_download_print'].includes(perm) ? '1' : '0'}`}
                className="w-full h-full border-0"
                data-testid="pdf-iframe"
                // sandbox is intentionally loose to allow PDF rendering in-browser
              />
            )
          ) : (
            <div className="flex items-center justify-center h-full text-gray-500">No access.</div>
          )}
        </div>

        {/* Footer watermark disclaimer */}
        <div className="px-5 py-2 border-t bg-gray-50 flex items-center justify-between text-[11px] text-gray-500">
          <div className="flex items-center gap-2">
            <AlertTriangle size={12} />
            <span>Viewing is logged and watermarked with your email, IP address and timestamp.</span>
          </div>
          {(perm === 'view_only' || perm === 'redacted') && (
            <span>Page {currentPage} of {pageCount}</span>
          )}
        </div>
      </div>
    </div>
  );
}

function ViewOnlyPager({ imgSrc, currentPage, pageCount, setCurrentPage }) {
  return (
    <div className="flex flex-col h-full">
      <div className="flex-1 overflow-auto flex items-start justify-center p-4">
        {imgSrc ? (
          <img
            src={imgSrc}
            alt={`Page ${currentPage}`}
            className="max-w-full shadow-lg bg-white"
            draggable={false}
            onContextMenu={(e) => e.preventDefault()}
            style={{ userSelect: 'none', pointerEvents: 'none' }}
            data-testid="page-image"
          />
        ) : (
          <div className="text-gray-500 mt-20">Loading page...</div>
        )}
      </div>
      <div className="flex items-center justify-center gap-3 py-3 border-t bg-white">
        <button
          disabled={currentPage <= 1}
          onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
          className="p-1.5 border rounded disabled:opacity-40 hover:bg-gray-50"
          data-testid="prev-page"
        >
          <ChevronLeft size={16} />
        </button>
        <span className="text-sm text-gray-700">
          Page {currentPage} of {pageCount}
        </span>
        <button
          disabled={currentPage >= pageCount}
          onClick={() => setCurrentPage(p => Math.min(pageCount, p + 1))}
          className="p-1.5 border rounded disabled:opacity-40 hover:bg-gray-50"
          data-testid="next-page"
        >
          <ChevronRight size={16} />
        </button>
      </div>
    </div>
  );
}
